import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import { removeLoadingKey } from '../helpers/common';
import { toast } from 'react-toastify';

export const ShipmentsLoadingKeys = {
    shipments: "ALL_SHIPMENTS",
    stats: "ALL_SHIPMENTS_STATS",
    sync: "SYNC_SHIPMENT",
    printAll: "PRINT_ALL_LABELS",
    printAllContainers: "PRINT_ALL_CONTAINERS",
    printByProduct: "PRINT_BY_PRODUCT",
    print: "PRINT_LABEL",
    printContainer: "PRINT_CONTAINER",
    printPackingPlan: "PRINT_PACKING_PLAN",
    addShipment: "ADD_SHIPMENT",
    addContainer: "ADD_CONTAINER",
    addItemsToContainer: "ADD_ITEMS_TO_CONTAINER",
    deleteLabel: "DELETE_LABEL",
    deleteBox: "DELETE_BOX",
    deleteContainer: "DELETE_CONTAINER",
    deleteShipment: "DELETE_SHIPMENT",
    updateBoxes: "UPDATE_BOXES",
    updateBox: "UPDATE_BOX",
    updateContainerItem: "UPDATE_CONTAINER_ITEM",
    updateContainer: "UPDATE_CONTAINER",
    getShipmentChangeLog: "GET_SHIPMENT_CHANGE_LOG",
    rollbackShipmentChanges: "ROLLBACK_SHIPMENT_CHANGES",
    undoLastShipmentBatch: "UNDO_LAST_SHIPMENT_BATCH",
    listPOS: "LIST_POS"
}

const defaultSuccessPringMessage = "The PDF file is generated and almost ready. The modal printing window will open in a few seconds.";

const initialState = {
    loading: true,
    loadingKeys: [],
    isFirstLoadComplete: false,
    error: null,
    shipments: [],
    pagination: {
      isTheLastPage: false,
      rangeRows: "",
      totalPages: 0
    },
    POs: [],
    syncingShipments: [],
    stats: {},
    shipmentChangeLog: [],
    shipmentChangeLogPagination: { total: 0, page: 1, limit: 100 },
}

export const getAllShipments = createAsyncThunk(
    'shipments/getAllShipments',
    async (params, { rejectWithValue }) => {
      try {
        const { data } = await axios.get(`/api/v1/shipments`, { params: { 
          search: params?.search, 
          statuses: params?.statuses,
          vendorIds: params?.vendorIds,
          eta: params?.eta,
          page: params?.page,
          itemsPerPage: params?.itemsPerPage
        } });
        return data;
      } catch (error) {
        if (error.code === "ERR_CANCELED" || error.message === "canceled") {
          throw new Error("AbortError");
        }
        if ('string' === typeof error.response.data) {
          return rejectWithValue(error.response.data)
        } else {
          return rejectWithValue(error.response.data.errors || error.response.data.error)
        }
      }
    }
)

export const getListPO = createAsyncThunk(
  'shipments/getListPO',
  async ({}, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`/api/v1/shipments/list`);
      return data;
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue(error.response.data)
      } else {
        return rejectWithValue(error.response.data.errors || error.response.data.error)
      }
    }
  }
)

export const getShipmentChangeLog = createAsyncThunk(
  'shipments/getShipmentChangeLog',
  async ({ poId, search, limit = 100, page = 1 } = {}, { rejectWithValue }) => {
    try {
      const { data } = await axios.get('/api/v1/shipment-change-log', {
        params: { poId, search, limit, page },
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const rollbackShipmentChanges = createAsyncThunk(
  'shipments/rollbackShipmentChanges',
  async ({ changeLogId, batchId, note } = {}, { rejectWithValue }) => {
    try {
      const { data } = await axios.post('/api/v1/shipment-rollback', {
        changeLogId,
        batchId,
        note,
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const undoLastShipmentBatch = createAsyncThunk(
  'shipments/undoLastShipmentBatch',
  async ({ poId } = {}, { rejectWithValue }) => {
    try {
      const { data } = await axios.post('/api/v1/shipment-undo-last', { poId });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const syncShipment = createAsyncThunk(
  'shipments/syncShipment',
  async ({ id }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`/api/v1/shipment/sync/${id}`);
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({ ...data });
        }, 2000);
      });
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data, id })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error, id })
      }
    }
  }
)

export const printAllContainers = createAsyncThunk(
  'shipments/printAllContainers',
  async ({ shipmentId, shipmentContainers }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`/api/v1/shipment/containers/print`, { shipmentId, shipmentContainers },{ timeout: 180000 });
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error })
      }
    }
  }
)

export const printAllPOLabels = createAsyncThunk(
  'shipments/printAllPOLabels',
  async ({ id, shipmentBoxes }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`/api/v1/shipment/labels/print`, { id, shipmentBoxes },{ timeout: 180000 });
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data, body: id })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error, body: id })
      }
    }
  }
)

export const printContainerData = createAsyncThunk(
  'shipments/printContainerData',
  async ({ containerId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`/api/v1/shipment/container/print`, { containerId });
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data, containerId })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error, containerId })
      }
    }
  }
)

export const printPackingPlan = createAsyncThunk(
  'shipments/printPackingPlan',
  async ({ containerId }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`/api/v1/shipment/container/packing-plan`, { containerId });
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data, containerId })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error, containerId })
      }
    }
  }
)

export const printPOLabelData = createAsyncThunk(
  'shipments/printPOLabel',
  async ({ poData }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`/api/v1/shipment/label/print`, poData);
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data, poData })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error, poData })
      }
    }
  }
)

export const printAllProductLabels = createAsyncThunk(
  'shipments/printAllProductLabels',
  async (body, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`/api/v1/shipment/product-labels/print`, body);
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data, body })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error, body })
      }
    }
  }
)

export const addContainerToThePO = createAsyncThunk(
  'shipments/addContainerToThePO',
  async ({ 
    containerName, 
    poId, 
    productData,
    search, 
    statuses,
    vendorIds,
    eta,
    page, 
    itemsPerPage,
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(`/api/v1/shipment/add-container`, { 
        containerName, poId, productData
      });
      dispatch(getAllShipments({ 
        search, 
        statuses,
        vendorIds,
        eta,
        page, 
        itemsPerPage,
      }));
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data, productData, poId })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error, productData, poId })
      }
    }
  }
)

export const addItemsToContainer = createAsyncThunk(
  'shipments/addItemsToContainer',
  async ({
    containerId,
    poId,
    productData,
    search,
    statuses,
    vendorIds,
    eta,
    page,
    itemsPerPage,
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(`/api/v1/shipment/container/items`, {
        containerId,
        poId,
        productData,
      });
      dispatch(getAllShipments({
        search,
        statuses,
        vendorIds,
        eta,
        page,
        itemsPerPage,
      }));
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data, productData, poId, containerId });
      }
      return rejectWithValue({
        error: error?.response?.data?.errors || error?.response?.data?.error,
        productData,
        poId,
        containerId,
      });
    }
  }
);

export const addShipmentElement = createAsyncThunk(
  'shipments/addShipmentElement',
  async ({ 
    poData, 
    poId, 
    search, 
    statuses,
    vendorIds,
    eta,
    page, 
    itemsPerPage,
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(`/api/v1/shipment/add-shipment`, { poData, poId });
      dispatch(getAllShipments({ 
        search, 
        statuses,
        vendorIds,
        eta,
        page, 
        itemsPerPage,
      }));
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data, poData, poId })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error, poData, poId })
      }
    }
  }
)

export const deleteShipmentLabel = createAsyncThunk(
  'shipments/deleteShipmentLabel',
  async ({ id }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`/api/v1/shipment/label/${id}`);
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data, id })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error, id })
      }
    }
  }
)

export const updateBoxes = createAsyncThunk(
  'shipments/updateBoxes',
  async ({ 
    boxesIds,
    shippingMethod,
    trackingNumber,
    receivedDate,
    dateShipped,
    toDelete
  }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`/api/v1/shipment/boxes`, {
        boxesIds,
        shippingMethod,
        trackingNumber,
        receivedDate,
        dateShipped,
        toDelete
      });
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error })
      }
    }
  }
)

export const deleteContainer = createAsyncThunk(
  'shipments/deleteContainer',
  async ({ 
    id, 
    search, 
    statuses,
    vendorIds,
    eta,
    page, 
    itemsPerPage,
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.delete(`/api/v1/container/${id}`);
      dispatch(getAllShipments({ 
        search, 
        statuses,
        vendorIds,
        eta,
        page, 
        itemsPerPage,
      }));
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data, id })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error, id })
      }
    }
  }
)

export const deleteBox = createAsyncThunk(
  'shipments/deleteBox',
  async ({ 
    id, 
    search, 
    statuses,
    vendorIds,
    eta,
    page, 
    itemsPerPage,
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.delete(`/api/v1/boxes/${id}`);
      dispatch(getAllShipments({ 
        search, 
        statuses,
        vendorIds,
        eta,
        page, 
        itemsPerPage,
      }));
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data, id })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error, id })
      }
    }
  }
)

export const deleteShipment = createAsyncThunk(
  'shipments/deleteShipment',
  async ({ 
    id, 
    search, 
    statuses,
    vendorIds,
    eta,
    page, 
    itemsPerPage,
  }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.delete(`/api/v1/shipment/${id}`);
      dispatch(getAllShipments({ 
        search, 
        statuses,
        vendorIds,
        eta,
        page, 
        itemsPerPage,
      }));
      return data;
    } catch (error) {
      if ('string' === typeof error?.response?.data) {
        return rejectWithValue({ error: error?.response?.data, id })
      } else {
        return rejectWithValue({ error: error?.response?.data?.errors || error?.response?.data?.error, id })
      }
    }
  }
)

export const updateContainer = createAsyncThunk(
  'shipments/updateContainer',
  async ({ 
    containers, 
    search, 
    statuses,
    vendorIds,
    eta,
    page, 
    itemsPerPage,
  }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post(`/api/v1/containers`, containers);
      await dispatch(getAllShipments({ 
        search, 
        statuses,
        vendorIds,
        eta,
        page, 
        itemsPerPage,
      }));
      return response.data;
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue({ error: error.response.data, containers })
      } else {
        return rejectWithValue({ error: error.response.data.errors || error.response.data.error, containers })
      }
    }
  }
)

export const updateContainerItem = createAsyncThunk(
  'shipments/updateContainerItem',
  async ({ 
    containerItems, 
    search, 
    statuses,
    vendorIds,
    eta,
    page, 
    itemsPerPage,
    source,
  }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post(`/api/v1/container-items${source ? `?source=${encodeURIComponent(source)}` : ''}`, containerItems);
      await dispatch(getAllShipments({ 
        search, 
        statuses,
        vendorIds,
        eta,
        page, 
        itemsPerPage,
      }));
      return response.data;
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue({ error: error.response.data, containerItems })
      } else {
        return rejectWithValue({ error: error.response.data.errors || error.response.data.error, containerItems })
      }
    }
  }
)

export const updateBox = createAsyncThunk(
  'shipments/updateBox',
  async ({ 
    boxes, 
    search, 
    statuses,
    vendorIds,
    eta,
    page, 
    itemsPerPage,
    source,
  }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post(`/api/v1/boxes${source ? `?source=${encodeURIComponent(source)}` : ''}`, boxes);
      await dispatch(getAllShipments({ 
        search, 
        statuses,
        vendorIds,
        eta,
        page, 
        itemsPerPage,
      }));
      return response.data;
    } catch (error) {
      if ('string' === typeof error.response.data) {
        return rejectWithValue({ error: error.response.data, boxes })
      } else {
        return rejectWithValue({ error: error.response.data.errors || error.response.data.error, boxes })
      }
    }
  }
)

export const shipmentsSlice = createSlice({
    name: 'shipments',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
      builder
        .addCase(getAllShipments.pending, (state) => {
            state.loading = true;
            state.loadingKeys = [...state.loadingKeys, ShipmentsLoadingKeys.shipments];
        })
        .addCase(getAllShipments.fulfilled, (state, action) => {
            state.loading = false;
            state.shipments = action.payload?.items;
            state.isFirstLoadComplete = true;
            state.pagination.isTheLastPage = action.payload?.isTheLastPage;
            state.pagination.rangeRows = action.payload?.rangeRows;
            state.pagination.totalPages = action.payload?.totalPages;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.shipments, state.loadingKeys);
        })
      .addCase(getAllShipments.rejected, (state, action) => {
            if (action.error?.message === "AbortError") {
              return;
            }
            state.loading = false;
            state.purchaseOrders = [];
            state.isFirstLoadComplete = true;
            state.pagination.isTheLastPage = false;
            state.pagination.rangeRows = "";
            state.pagination.totalPages = 0;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.shipments, state.loadingKeys);;
        })
        .addCase(getShipmentChangeLog.pending, (state) => {
            state.loadingKeys = [...state.loadingKeys, ShipmentsLoadingKeys.getShipmentChangeLog];
        })
        .addCase(getShipmentChangeLog.fulfilled, (state, action) => {
            state.shipmentChangeLog = action.payload?.items || [];
            state.shipmentChangeLogPagination = {
                total: action.payload?.total || 0,
                page: action.payload?.page || 1,
                limit: action.payload?.limit || 100,
            };
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.getShipmentChangeLog, state.loadingKeys);
            state.error = null;
        })
        .addCase(getShipmentChangeLog.rejected, (state, action) => {
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.getShipmentChangeLog, state.loadingKeys);
            state.error = action.payload || action.error?.message;
        })
        .addCase(rollbackShipmentChanges.pending, (state) => {
            state.loadingKeys = [...state.loadingKeys, ShipmentsLoadingKeys.rollbackShipmentChanges];
        })
        .addCase(rollbackShipmentChanges.fulfilled, (state) => {
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.rollbackShipmentChanges, state.loadingKeys);
            state.error = null;
            toast.success('Shipping change reverted.', { position: 'bottom-right' });
        })
        .addCase(rollbackShipmentChanges.rejected, (state, action) => {
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.rollbackShipmentChanges, state.loadingKeys);
            state.error = action.payload || action.error?.message;
            toast.error(`Shipping change could not be reverted.`, { position: 'bottom-right' });
        })
        .addCase(undoLastShipmentBatch.pending, (state) => {
            state.loadingKeys = [...state.loadingKeys, ShipmentsLoadingKeys.undoLastShipmentBatch];
        })
        .addCase(undoLastShipmentBatch.fulfilled, (state) => {
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.undoLastShipmentBatch, state.loadingKeys);
            state.error = null;
            toast.success('Last shipping change batch reverted.', { position: 'bottom-right' });
        })
        .addCase(undoLastShipmentBatch.rejected, (state, action) => {
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.undoLastShipmentBatch, state.loadingKeys);
            state.error = action.payload || action.error?.message;
            toast.error(`No shipping changes could be undone.`, { position: 'bottom-right' });
        })
        .addCase(syncShipment.pending, (state, action) => {
          state.loading = true;
          state.syncingShipments = [...state.syncingShipments, action.meta?.arg?.id];
          state.loadingKeys = [...state.loadingKeys, ShipmentsLoadingKeys.sync];
        })
        .addCase(syncShipment.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;
            state.syncingShipments = state.syncingShipments.filter((id) => id !== action.meta?.arg?.id);
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.sync, state.loadingKeys);
            toast.success(
              `PO synced successfully.`, 
              { position: "bottom-right" }
            )
        })
        .addCase(syncShipment.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.syncingShipments = state.syncingShipments.filter((id) => id !== action.meta?.arg?.id);
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.sync, state.loadingKeys);
            const errorMessage = action?.payload?.error || action.error?.message;
            toast.error(
              `${errorMessage}`, { position: "bottom-right" }
            )
        })
        .addCase(updateBoxes.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ShipmentsLoadingKeys.updateBoxes];
        })
        .addCase(updateBoxes.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.updateBoxes, state.loadingKeys);
            toast.success(
              `Boxes is updated successfully.`, { position: "bottom-right" }
            )
        })
        .addCase(updateBoxes.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.updateBoxes, state.loadingKeys)
            toast.error(
              `Boxes can't be updated. Error - ${action.error?.message}.`, { position: "bottom-right" }
            )
        })
        .addCase(printAllContainers.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ShipmentsLoadingKeys.printAllContainers];
        })
        .addCase(printAllContainers.fulfilled, (state) => {
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.printAllContainers, state.loadingKeys);
            toast.success(
              defaultSuccessPringMessage, 
              { position: "bottom-right" }
            );
        })
        .addCase(printAllContainers.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.printAllContainers, state.loadingKeys);
            toast.error(
              `The PDF can't be generated. Error - ${action.error?.message}.`, { position: "bottom-right" }
            )
        })
        .addCase(printAllPOLabels.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ShipmentsLoadingKeys.printAll];
        })
        .addCase(printAllPOLabels.fulfilled, (state) => {
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.printAll, state.loadingKeys);
            toast.success(
              defaultSuccessPringMessage, 
              { position: "bottom-right" }
            );
        })
        .addCase(printAllPOLabels.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.printAll, state.loadingKeys);
            toast.error(
              `The PDF can't be generated. Error - ${action.error?.message}.`, { position: "bottom-right" }
            )
        })
        .addCase(printAllProductLabels.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ShipmentsLoadingKeys.printByProduct];
        })
        .addCase(printAllProductLabels.fulfilled, (state) => {
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.printByProduct, state.loadingKeys);
            toast.success(
              defaultSuccessPringMessage, 
              { position: "bottom-right" }
            );
        })
        .addCase(printAllProductLabels.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.printByProduct, state.loadingKeys);
            toast.error(
              `The PDF can't be generated. Error - ${action.error?.message}.`, { position: "bottom-right" }
            )
        })
        .addCase(printContainerData.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ShipmentsLoadingKeys.printContainer];
        })
        .addCase(printContainerData.fulfilled, (state) => {
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.printContainer, state.loadingKeys);
            toast.success(
              defaultSuccessPringMessage, 
              { position: "bottom-right" }
            );
        })
        .addCase(printContainerData.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.printContainer, state.loadingKeys);
            toast.error(
              `The PDF can't be generated. Error - ${action.error?.message}.`, { position: "bottom-right" }
            )
        })
        .addCase(printPackingPlan.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ShipmentsLoadingKeys.printPackingPlan];
        })
        .addCase(printPackingPlan.fulfilled, (state) => {
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.printPackingPlan, state.loadingKeys);
            toast.success(
              "Packing plan is ready.",
              { position: "bottom-right" }
            );
        })
        .addCase(printPackingPlan.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.printPackingPlan, state.loadingKeys);
            toast.error(
              `The PDF can't be generated. Error - ${action.error?.message}.`, { position: "bottom-right" }
            )
        })
        .addCase(printPOLabelData.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ShipmentsLoadingKeys.print];
        })
        .addCase(printPOLabelData.fulfilled, (state) => {
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.print, state.loadingKeys);
            toast.success(
              defaultSuccessPringMessage, 
              { position: "bottom-right" }
            );
        })
        .addCase(printPOLabelData.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.print, state.loadingKeys);
            toast.error(
              `The PDF can't be generated. Error - ${action.error?.message}.`, { position: "bottom-right" }
            )
        })
        .addCase(addShipmentElement.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ShipmentsLoadingKeys.addShipment];
        })
        .addCase(addShipmentElement.fulfilled, (state) => {
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.addShipment, state.loadingKeys);
        })
        .addCase(addShipmentElement.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.addShipment, state.loadingKeys);
        })
        .addCase(addContainerToThePO.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ShipmentsLoadingKeys.addContainer];
        })
        .addCase(addContainerToThePO.fulfilled, (state) => {
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.addContainer, state.loadingKeys);
            toast.success(`Container added successfully.`, { position: "bottom-right" })
        })
        .addCase(addContainerToThePO.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.addContainer, state.loadingKeys);
            toast.error(
              `Container can't be added. Error - ${action.error?.message}.`, { position: "bottom-right" }
            )
        })
        .addCase(addItemsToContainer.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ShipmentsLoadingKeys.addItemsToContainer];
        })
        .addCase(addItemsToContainer.fulfilled, (state) => {
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.addItemsToContainer, state.loadingKeys);
            toast.success(`Items added to container successfully.`, { position: "bottom-right" });
        })
        .addCase(addItemsToContainer.rejected, (state, action) => {
            state.loading = false;
            const error = action.payload?.error || action.error?.message;
            state.error = error;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.addItemsToContainer, state.loadingKeys);
            toast.error(
              `Items can't be added to container. Error - ${error}.`, { position: "bottom-right" }
            );
        })
        .addCase(deleteShipmentLabel.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ShipmentsLoadingKeys.deleteLabel];
        })
        .addCase(deleteShipmentLabel.fulfilled, (state) => {
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.deleteLabel, state.loadingKeys);
        })
        .addCase(deleteShipmentLabel.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.deleteLabel, state.loadingKeys);
        })
        .addCase(deleteBox.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ShipmentsLoadingKeys.deleteBox];
        })
        .addCase(deleteBox.fulfilled, (state) => {
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.deleteBox, state.loadingKeys);
            toast.success(`Box deleted successfully.`, { position: "bottom-right" })
        })
        .addCase(deleteBox.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.deleteBox, state.loadingKeys);
            toast.error(
              `Box can't be deleted. Error - ${action.error?.message}.`, { position: "bottom-right" }
            )
        })
        .addCase(deleteContainer.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ShipmentsLoadingKeys.deleteContainer];
        })
        .addCase(deleteContainer.fulfilled, (state) => {
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.deleteContainer, state.loadingKeys);
            toast.success(`Container deleted successfully.`, { position: "bottom-right" })
        })
        .addCase(deleteContainer.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.deleteContainer, state.loadingKeys);
            toast.error(
              `Container can't be deleted. Error - ${action.error?.message}.`, { position: "bottom-right" }
            )
        })
        .addCase(deleteShipment.pending, (state) => {
          state.loading = true;
          state.loadingKeys = [...state.loadingKeys, ShipmentsLoadingKeys.deleteShipment];
        })
        .addCase(deleteShipment.fulfilled, (state) => {
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.deleteShipment, state.loadingKeys);
            toast.success(`Shipment deleted successfully.`, { position: "bottom-right" })
        })
        .addCase(deleteShipment.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.deleteShipment, state.loadingKeys);
            toast.error(
              `Shipment can't be deleted. Error - ${action.error?.message}.`, { position: "bottom-right" }
            )
        })
        .addCase(updateContainer.pending, (state) => {
            state.loading = true
            state.loadingKeys = [...state.loadingKeys, ShipmentsLoadingKeys.updateContainer];
        })
        .addCase(updateContainer.fulfilled, (state) => {
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.updateContainer, state.loadingKeys);
            toast.success(`Container updated successfully.`, { position: "bottom-right" })
        })
        .addCase(updateContainer.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.updateContainer, state.loadingKeys);
            toast.error(
              `Container can't be updated. Error - ${action.error?.message}.`, { position: "bottom-right" }
            )
        })
        .addCase(updateContainerItem.pending, (state) => {
            state.loading = true
            state.loadingKeys = [...state.loadingKeys, ShipmentsLoadingKeys.updateContainerItem];
        })
        .addCase(updateContainerItem.fulfilled, (state) => {
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.updateContainerItem, state.loadingKeys);
            toast.success(`Container item updated successfully.`, { position: "bottom-right" })
        })
        .addCase(updateContainerItem.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.updateContainerItem, state.loadingKeys);
            toast.error(
              `Container item can't be updated. Error - ${action.error?.message}.`, { position: "bottom-right" }
            )
        })
        .addCase(updateBox.pending, (state) => {
            state.loading = true
            state.loadingKeys = [...state.loadingKeys, ShipmentsLoadingKeys.updateBox];
        })
        .addCase(updateBox.fulfilled, (state) => {
            state.loading = false;
            state.error = null;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.updateBox, state.loadingKeys);
            toast.success(`Box updated successfully.`, { position: "bottom-right" })
        })
        .addCase(updateBox.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.updateBox, state.loadingKeys);
            toast.error(
              `Box can't be updated. Error - ${action.error?.message}.`, { position: "bottom-right" }
            )
        })
        .addCase(getListPO.pending, (state) => {
          if (!Array.isArray(state.POs) || state.POs.length === 0) {
            state.loading = true
            state.loadingKeys = [...state.loadingKeys, ShipmentsLoadingKeys.listPOS];
          }
        })
        .addCase(getListPO.fulfilled, (state, action) => {
            state.loading = false;
            state.error = null;
            state.POs = action?.payload;
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.listPOS, state.loadingKeys);
        })
        .addCase(getListPO.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error?.message;
            state.POs = [];
            state.loadingKeys = removeLoadingKey(ShipmentsLoadingKeys.listPOS, state.loadingKeys);
        })
    }
})

export const {} = shipmentsSlice.actions;

export default shipmentsSlice.reducer